import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaTrashAlt, FaUndo, FaTrash, FaSearch, FaFolder, FaFileInvoice, FaFile, FaRegFileAlt, FaFilter, FaUser, FaDollarSign, FaEnvelope, FaSignInAlt, FaBell, FaBook, FaNewspaper } from 'react-icons/fa';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import EmptyState from '../../components/ui/EmptyState';

const TYPE_LABELS = {
  invoice: 'Invoice',
  document: 'Document',
  registeredAgentDocument: 'Reg. Agent Doc',
  folder: 'Folder',
  chat: 'Chat History',
  user: 'User Account',
  payment: 'Payment',
  emailLog: 'Email Log',
  loginHistory: 'Login History',
  member: 'Member Account',
  notification: 'Notification',
  bookkeepingFile: 'Bookkeeping File',
  blog: 'Blog Post'
};

const TYPE_ICON = {
  invoice: FaFileInvoice,
  document: FaFile,
  registeredAgentDocument: FaRegFileAlt,
  folder: FaFolder,
  chat: FaTrashAlt,
  user: FaUser,
  payment: FaDollarSign,
  emailLog: FaEnvelope,
  loginHistory: FaSignInAlt,
  member: FaUser,
  notification: FaBell,
  bookkeepingFile: FaBook,
  blog: FaNewspaper
};

const TYPE_COLORS = {
  invoice: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  document: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  registeredAgentDocument: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  folder: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  chat: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  user: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  payment: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  emailLog: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  loginHistory: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  member: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  notification: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  bookkeepingFile: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  blog: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

// Removed getDaysRemaining as auto-delete is no longer supported

const getItemName = (item) => {
  const d = item.itemData;
  if (!d) return `Item #${item.itemId?.slice(-6)}`;

  if (item.itemType === 'chat') {
    if (d.fileUrl) return `File: ${d.originalName || 'Unnamed file'}`;
    return d.message ? (d.message.length > 30 ? d.message.substring(0, 30) + '...' : d.message) : 'Empty message';
  }

  if (item.itemType === 'payment') {
    return d.service || d.serviceName || d.paymentId || (d.amount != null ? `Payment $${d.amount}` : `Payment (${item.itemId?.slice(-6)})`);
  }

  if (item.itemType === 'notification') {
    return d.title || (d.message ? (d.message.length > 40 ? d.message.substring(0, 40) + '...' : d.message) : 'Notification');
  }

  if (item.itemType === 'user' || item.itemType === 'member') {
    return d.name || d.email || TYPE_LABELS[item.itemType];
  }

  if (item.itemType === 'emailLog') {
    return d.subject || d.emailType || 'Email Log';
  }

  if (item.itemType === 'loginHistory') {
    return `${d.eventType || 'login'} — ${d.userName || d.userEmail || 'Unknown'}`;
  }

  if (item.itemType === 'blog') {
    const status = d.status ? ` (${d.status})` : '';
    return (d.title || d.slug || 'Blog Post') + status;
  }

  return d.originalName || d.name || d.title || `${TYPE_LABELS[item.itemType] || 'Item'} (${item.itemId?.slice(-6)})`;
};

const RecycleBin = () => {
  const [items, setItems] = useState([]);
  const [typeCounts, setTypeCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/recycle-bin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load recycle bin');
      const data = await res.json();
      setItems(data.items || []);
      setTypeCounts(data.typeCounts || {});
      setTotalCount(data.total ?? (data.items || []).length);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleRestore = async (id) => {
    setActionLoading(id + '_restore');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/recycle-bin/${id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Restore failed');
      showToast('Item restored successfully!', 'success');
      fetchItems();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = async (id) => {
    setConfirmDelete(null);
    setActionLoading(id + '_delete');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/recycle-bin/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      showToast('Item permanently deleted.', 'success');
      setItems(prev => prev.filter(i => i._id !== id));
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = items.filter(item => {
    const name = getItemName(item).toLowerCase();
    const matchesSearch = name.includes(search.toLowerCase()) ||
      item.userName?.toLowerCase().includes(search.toLowerCase()) ||
      item.userEmail?.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || item.itemType === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filtered.map(i => i._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkRestore = async () => {
    if (selectedIds.length === 0) return;
    setActionLoading('bulk_restore');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/recycle-bin/bulk-restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds })
      });
      if (!res.ok) throw new Error('Bulk restore failed');
      showToast(`${selectedIds.length} items restored successfully!`, 'success');
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedIds.length} items?`)) return;

    setActionLoading('bulk_delete');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/recycle-bin/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: selectedIds })
      });
      if (!res.ok) throw new Error('Bulk delete failed');
      showToast(`${selectedIds.length} items permanently deleted.`, 'success');
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-xl shadow-xl text-white font-semibold text-sm animate-fade-in-up
          ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-red-100 dark:bg-red-900/30">
            <FaTrashAlt className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Recycle Bin</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Manage deleted items here. They will stay until permanently deleted.
            </p>
          </div>
        </div>
        <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm font-semibold">
          {totalCount} item{totalCount !== 1 ? 's' : ''}
        </span>
      </div>

      {totalCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(typeCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([type, count]) => (
              <button
                key={type}
                type="button"
                onClick={() => setTypeFilter(typeFilter === type ? 'all' : type)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  typeFilter === type
                    ? 'ring-2 ring-red-400 ring-offset-1 dark:ring-offset-dark-900'
                    : ''
                } ${TYPE_COLORS[type] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
              >
                {TYPE_LABELS[type] || type}
                <span className="opacity-75">({count})</span>
              </button>
            ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by name, user..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-400 transition-all"
          />
        </div>
        {/* Type Filter */}
        <div className="relative">
          <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="pl-8 pr-4 py-2.5 rounded-xl bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-400 appearance-none cursor-pointer transition-all min-w-[160px]"
          >
            <option value="all">All Types</option>
            <option value="user">User Accounts</option>
            <option value="invoice">Invoices</option>
            <option value="document">Documents</option>
            <option value="registeredAgentDocument">Reg. Agent Docs</option>
            <option value="folder">Folders</option>
            <option value="chat">Chats</option>
            <option value="payment">Payments</option>
            <option value="emailLog">Email Logs</option>
            <option value="loginHistory">Login History</option>
            <option value="member">Member Accounts</option>
            <option value="notification">Notifications</option>
            <option value="bookkeepingFile">Bookkeeping Files</option>
            <option value="blog">Blog Posts</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-xl animate-fade-in">
          <span className="text-sm font-semibold text-red-700 dark:text-red-400">
            {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={actionLoading === 'bulk_restore' || actionLoading === 'bulk_delete'}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {actionLoading === 'bulk_restore' ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaUndo className="w-4 h-4" />}
              Restore Selected
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={actionLoading === 'bulk_restore' || actionLoading === 'bulk_delete'}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {actionLoading === 'bulk_delete' ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaTrash className="w-4 h-4" />}
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <SkeletonLoader variant="table" rows={6} cols={5} />
      ) : error ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-red-500 font-medium mb-3">{error}</p>
            <button onClick={fetchItems} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors">Retry</button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FaTrashAlt}
          title="Recycle Bin is Empty"
          description={search || typeFilter !== 'all'
            ? 'No items match your search or filter.'
            : 'Deleted files, folders, users, chats, payments, notifications, and more will appear here.'}
          preset={search || typeFilter !== 'all' ? 'search' : 'folder'}
        />
      ) : (
        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-dark-800">
                  <th className="px-6 py-3.5 w-10 relative">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && selectedIds.length === filtered.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-red-500 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                    />
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2 py-3.5">Item</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3.5">Type</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3.5">Client</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 py-3.5">Deleted On</th>
                  <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-6 py-3.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-dark-800">
                {filtered.map((item) => {
                  const Icon = TYPE_ICON[item.itemType] || FaFile;
                  const isSelected = selectedIds.includes(item._id);
                  const isRestoring = actionLoading === item._id + '_restore';
                  const isDeleting = actionLoading === item._id + '_delete';
                  return (
                    <tr key={item._id} className={`${isSelected ? 'bg-red-50/50 dark:bg-red-900/10' : 'hover:bg-gray-50 dark:hover:bg-dark-800/50'} transition-colors`}>
                      <td className="px-6 py-4 relative">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectOne(item._id)}
                          className="w-4 h-4 text-red-500 bg-white border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-dark-900 dark:border-dark-700 cursor-pointer"
                        />
                      </td>
                      <td className="px-2 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-gray-100 dark:bg-dark-800 flex-shrink-0">
                            <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate">{getItemName(item)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${TYPE_COLORS[item.itemType] || ''}`}>
                          {TYPE_LABELS[item.itemType] || item.itemType}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900 dark:text-white">{item.userName || '—'}</p>
                          <p className="text-gray-400 dark:text-gray-500 text-xs truncate max-w-[160px]">{item.userEmail}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(item.deletedAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleRestore(item._id)}
                            disabled={isRestoring || isDeleting}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-xs font-semibold transition-all disabled:opacity-50"
                          >
                            {isRestoring ? <span className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /> : <FaUndo className="w-3 h-3" />}
                            Restore
                          </button>
                          <button
                            onClick={() => setConfirmDelete(item._id)}
                            disabled={isRestoring || isDeleting}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 text-xs font-semibold transition-all disabled:opacity-50"
                          >
                            {isDeleting ? <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> : <FaTrash className="w-3 h-3" />}
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-900 rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 border border-gray-200 dark:border-dark-700 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30">
                <FaTrash className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">Permanent Delete</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to permanently delete this item? It will be gone forever.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-700 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePermanentDelete(confirmDelete)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-colors"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RecycleBin;
